<template>
    <div
        class="drag-pipeline-columns"
        :id="pipeline._id"
        :class="{ 'minimized': isMinimized }"
        :style="[
            moving && {
                border: '3px dotted rgb(163, 163, 170)',
                backgroundColor: 'rgb(163 163 170 / 20%)',
                borderRadius: '8px',
            },
            { '--pipeline-color': pipeline.color },
            { '--pipeline-bg-color': taskBackgroundColor() },
        ]"
    >
        <div class="column-content" v-show="!isMinimized">
            <div
                class="drag-pipeline-column-head"
                :style="{
                    backgroundColor: taskBackgroundColor(),
                    borderLeft: `7px solid ${pipeline.color || 'black'}`,
                }"
                :type="pipeline.type"
            >
                <div class="pipeline-head-content">
                    <div class="pipeline-details">
                        <div class="pipeline-detail">
                            <div class="column-check" v-if="pipeline.isNewColumn || editMode">
                                <div class="pipeline-picker" :style="{ '--row-color': pipeline.color }">
                                    <div class="material-icons">palette</div>
                                    <input type="color" style="opacity: 0" v-model="pipeline.color" />
                                </div>
                                <a-input
                                    :placeholder="$t('task.writeSomething')"
                                    class="basic-task-input"
                                    v-model="pipeline.name"
                                />
                            </div>
                            <div class="column-check" v-else>
                                <a-checkbox
                                    class="kanban-checkbox"
                                    :indeterminate="indeterminate"
                                    :checked="selected"
                                    @change="checkedAllRows"
                                />
                                {{ pipeline.name }}
                            </div>
                            <div class="drag-pipeline-column-rows-length" v-if="!pipeline.isNewColumn && !editMode">
                                <template v-if="pipeline.filteredTaskCount !== undefined && pipeline.filteredTaskCount !== pipeline.taskCount">
                                    {{ pipeline.filteredTaskCount || 0 }}/{{ pipeline.taskCount || 0 }} {{ $t("task.task") }}
                                </template>
                                <template v-else>
                                    {{ pipeline.taskCount || 0 }} {{ $t("task.task") }}
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="column-options">
                        <a-popover
                            overlayClassName="task-more-options"
                            v-if="!pipeline.isNewColumn && !editMode"
                            trigger="click"
                            placement="bottomRight"
                        >
                            <div slot="content">
                                <div class="options-item" @click="openEditMode">
                                    <div class="material-icons">edit</div>
                                    {{ $t("settings.edit") }}
                                </div>
                                <div class="options-item" @click="copyPipelineId">
                                    <div class="material-icons">content_copy</div>
                                    Pipeline Id Kopyala
                                </div>
                                <div
                                    class="options-item"
                                    v-if="isDeletedColumn()"
                                    @click="deleteColumn"
                                >
                                    <div class="material-icons">delete</div>
                                    {{ $t("chatbotv2.delete") }}
                                </div>
                            </div>
                            <div class="column-options">
                                <div class="task-more material-icons">more_vert</div>
                            </div>
                        </a-popover>
                        <div class="column-options" v-if="pipeline.isNewColumn || editMode" @click="createPipeline">
                            <div class="task-more material-icons">check</div>
                        </div>
                        <div class="column-options" v-if="pipeline.isNewColumn || editMode" @click="deletePipeline">
                            <div class="task-more material-icons">close</div>
                        </div>
                        <div
                            class="column-options"
                            v-if="!pipeline.isNewColumn && !editMode && pipeline.type !== 'completed'"
                            @click="$feature.beforeCreateTaskColumn(addNewColumn, getPipelineCount())"
                        >
                            <div class="task-more material-icons">add</div>
                        </div>
                        <a-button
                            class="collapse-button"
                            v-if="!pipeline.isNewColumn && !editMode"
                            @click="toggleMinimize"
                        >
                            <span class="material-icons">unfold_less</span>
                        </a-button>
                    </div>
                </div>
                <div class="basic-task-container" v-if="!pipeline.isNewColumn && !editMode">
                    <div
                        class="basic-task-add"
                        @click="$feature.beforeCreateTaskRow(() => createTaskForm('external'), pipeline.taskCount)"
                    >
                        <span class="material-icons"> add </span>
                    </div>
                </div>
            </div>

            <BasicTask
                v-if="isTaskFormActive('external')"
                :basicTaskForm="basicTaskForm"
                :pipelineStageId="pipeline._id"
                :workflow-id="workflowId"
                @close="() => closeTaskForm('external')"
                @createdTask="$emit('createdTask', $event)"
                @updateBasicTaskForm="$emit('updateBasicTaskForm', $event)"
            />
            <draggable
                v-if="!pipeline.isNewColumn"
                :options="groupTaskItemDragOptions"
                v-model="cloneTasks"
                :id="pipeline._id"
                @end="moveActionEnd"
                group="tasks"
                class="drag-pipeline-rows"
                ref="pipeline"
                draggable=".drag-pipeline-row"
            >
                <TaskCard
                    v-for="task of cloneTasks"
                    :key="task._id"
                    :item="task"
                    :pipeline="pipeline"
                    :workflow-id="workflowId"
                    class="task-card"
                    :checked="checkedItems[task._id]"
                    @checked="checkedTask"
                    @delete-item="deleteTaskModalOpen"
                    @confirm-item="confirmModelOpen"
                    @archived-item="archivedTask"
                    @change-workflow="changeWorkflow"
                    @to-completed="toCompleted"
                />
                <div
                    v-if="!isTaskFormActive('internal') && !pipeline.isNewColumn && !editMode"
                    class="basic-task-add no-drag"
                    key="add-button-inside"
                    @click="$feature.beforeCreateTaskRow(() => createTaskForm('internal'), pipeline.taskCount)"
                >
                    <span class="material-icons">add</span>
                    <span style="margin-left: 8px">{{ $t("message.createSubject") }}</span>
                </div>
                <BasicTask
                    v-if="isTaskFormActive('internal')"
                    :key="'basic-task-form'"
                    :basicTaskForm="basicTaskForm"
                    class="no-drag"
                    :pipelineStageId="pipeline._id"
                    :workflow-id="workflowId"
                    @createdTask="$emit('createdTask', $event)"
                    @close="() => closeTaskForm('internal')"
                    @updateBasicTaskForm="$emit('updateBasicTaskForm', $event)"
                />
                <div v-if="isLoading" class="loading-indicator no-drag">
                    <div class="loading-spinner"></div>
                    <div class="loading-text">Yükleniyor...</div>
                </div>
            </draggable>
        </div>

        <div class="minimize-toggle" v-if="isMinimized" @click="toggleMinimize">
            <div class="minimize-toggle-icon">
                <span class="material-icons">chevron_right</span>
            </div>
            <div class="minimize-toggle-count">
                <template v-if="pipeline.filteredTaskCount !== undefined && pipeline.filteredTaskCount !== pipeline.taskCount">
                    {{ pipeline.filteredTaskCount || 0 }}/{{ pipeline.taskCount || 0 }}
                </template>
                <template v-else>
                    {{ pipeline.taskCount || 0 }}+
                </template>
            </div>
            <div class="minimize-toggle-title">
                {{ pipeline.name }}
            </div>
        </div>

        <DeleteModal
            v-if="deleteColumnModal"
            :description="$t('task.deleteColumnCont')"
            :btnCancelText="$t('message.cancel')"
            :btnoktext="$t('message.delete')"
            @cancel="deleteColumnModal = false"
            @confirm="deletePipelineFn(false)"
        />
        <DeleteModal v-if="deleteTransportModal" @cancel="deleteTransportModal = false" :modalBtnVisible="false">
            <div v-if="transportTaskBtn">
                <a-button type="danger" @click="transportColumnVisibility">
                    {{ $t("task.moveAndDeleteTask") }}
                </a-button>
                <a-button type="danger" @click="deletePipelineFn(true)">
                    {{ $t("task.justDelete") }}
                </a-button>
            </div>
            <div v-if="transportTaskNewColumn" class="kanban-task-transport-select">
                <p>{{ $t("task.selectColumnMoveTask") }}</p>
                <a-select
                    class="classic-new-select"
                    v-model="newTaskPositionId"
                    style="margin-bottom: 10px; width: 200px"
                >
                    <a-select-option v-for="item in getOtherPipelines()" :key="item._id" :value="item._id">
                        {{ item.name }}
                    </a-select-option>
                </a-select>
                <a-button type="danger" @click="deletePipelineFn(true)">Sil</a-button>
            </div>
        </DeleteModal>
    </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from "vuex";
import TaskCard from "./TaskCard.vue";
import BasicTask from "../../../../components/task/BasicTaskForm";
import Draggable from "vuedraggable";
import DeleteModal from "./DeleteModal.vue";
import { PERMISSION_TYPES } from "../../../../language/permissionTypes";

export default {
    name: "TaskColCard",
    components: {
        TaskCard,
        BasicTask,
        Draggable,
        DeleteModal,
    },
    props: {
        workflowId: {
            type: String,
            default: null,
        },
        pipelineLoaded: {
            type: Boolean,
            default: false,
        },
        pipelines: {
            type: Array,
            default: () => [],
        },
        pipeline: {
            type: Object,
            default: {},
        },
        moving: {
            type: Boolean,
            default: false,
        },
        tasks: {
            type: Array,
            default: () => [],
        },
        checkedItems: {
            type: Object,
            default: () => ({}),
        },
        basicTaskForm: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            isLoading: false,
            cloneTasks: [],
            isMinimized: false,
            groupTaskItemDragOptions: {
                tasks: "tasks",
                animation: 100,
                draggable: ".task-card",
                filter: ".no-drag, .basic-task-add, .basic-task",
                preventOnFilter: true,
            },
            selected: false,
            indeterminate: false,
            editMode: false,
            options: [
                {
                    value: "todo",
                    label: this.$t("task.todo"),
                },
                {
                    value: "in-progress",
                    label: this.$t("task.inProgress"),
                },
                {
                    value: "approval",
                    label: this.$t("task.approval"),
                },
                {
                    value: "completed",
                    label: this.$t("task.completed"),
                },
            ],
            listeners: [],
            deleteColumnModal: false,
            deleteTransportModal: false,
            newTaskPositionId: null,
            transportTaskNewColumn: false,
            transportTaskBtn: true,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
    },
    methods: {
        ...mapActions({
            deletePipelineStage: "tasks/deletePipelineStage",
        }),
        toggleMinimize() {
            this.isMinimized = !this.isMinimized;
        },
        getPipelineCount() {
            return this.pipelines.length;
        },
        getOtherPipelines() {
            return this.pipelines.filter((item) => item._id !== this.pipeline._id);
        },
        taskBackgroundColor() {
            let color = this.pipeline.color || "#000000";
            let codes = color.slice(1);
            let first = parseInt(codes.slice(0, 2), 16);
            let second = parseInt(codes.slice(2, 4), 16);
            let third = parseInt(codes.slice(4, 6), 16);
            return `rgba(${first}, ${second}, ${third}, 0.2)`;
        },
        handleAddNewColumn() {
            const pipelineCount = this.getPipelineCount();
            this.$feature.beforeCreateTaskColumn(() => {
                this.addNewColumn();
            }, pipelineCount);
        },
        addNewColumn() {
            this.$emit("pipeline-form", this.pipeline);
        },
        isTaskFormActive(context) {
            return this.basicTaskForm[this.pipeline._id]?.context === context;
        },
        checkedTask(item) {
            this.$emit("updateCheckedItems", {
                ...this.checkedItems,
                [item.taskId]: item.value,
            });
        },
        createTaskForm(context) {
            const workflow = this.workflows[this.workflowId];
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(
                (permission) => workflow.permissions[permission]
            );
            const perm = permissions.every((permission) => permission.length === 0);
            if (perm || permissions[0].includes(this.user._id)) {
                this.$emit("updateBasicTaskForm", {
                    [this.pipeline._id]: {
                        enabled: true,
                        context: context,
                        form: this.basicTaskForm[this.pipeline._id]?.form || {},
                    },
                });
            } else {
                this.$notification.error({
                    message: this.$t("task.createTaskError"),
                    description: this.$t("task.createTaskErrorDesc"),
                });
            }
        },
        closeTaskForm(context) {
            if (this.basicTaskForm[this.pipeline._id]?.context === context) {
                this.$emit("updateBasicTaskForm", {
                    [this.pipeline._id]: {
                        enabled: false,
                        context: null,
                        form: {},
                    },
                });
            }
        },
        deleteTaskModalOpen(task) {
            const workflow = this.workflows[this.workflowId];
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(
                (permission) => workflow.permissions[permission]
            );
            const perm = permissions.every((permission) => permission.length === 0);
            if (perm || permissions[3].includes(this.user._id)) {
                this.$emit("delete-modal-task", task);
            } else {
                this.$notification.error({
                    message: this.$t("task.deleteTaskError"),
                    description: this.$t("task.deleteTaskErrorDesc"),
                });
            }
        },
        confirmModelOpen(task) {
            const workflow = this.workflows[this.workflowId];
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask", "archiveTask", "doneTask"].map(
                (permission) => workflow.permissions[permission]
            );
            const perm = permissions.every((permission) => permission.length === 0);
            if (perm || permissions[5].includes(this.user._id)) {
                this.$emit("confirm-modal-task", task);
            } else {
                this.$notification.error({
                    message: "Görev Kapatma Yetkiniz Yok.",
                    description: "Görevi kapatmak için yetkiniz yok yöneticinizle iletişime geçiniz",
                });
            }
        },
        archivedTask(task) {
            const workflow = this.workflows[this.workflowId];
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask", "archiveTask"].map(
                (permission) => workflow.permissions[permission]
            );
            const perm = permissions.every((permission) => permission.length === 0);
            if (perm || permissions[4].includes(this.user._id)) {
                this.$emit("archived-modal-task", task);
            } else {
                this.$notification.error({
                    message: "Görev Arşivleme Yetkiniz Yok",
                    description: "Görevi arşivlemek için yetkiniz yok yöneticinizle iletişime geçiniz",
                });
            }
        },
        changeWorkflow(task) {
            this.$emit("change-workflow-task", task);
        },
        toCompleted(task) {
            this.$emit("to-completed-task", task);
        },
        moveActionEnd(event) {
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(
                (permission) => this.workflows[this.workflowId].permissions[permission]
            );
            const perm = permissions.every((permission) => permission.length === 0);
            if (perm || permissions[2].includes(this.user._id)) {
                this.$emit("moved", event, this.cloneTasks);
                return;
            }
            this.$notification.error({
                message: this.$t("task.moveTaskError"),
                description: this.$t("task.moveTaskErrorDesc"),
            });
            this.$emit("resetActions");
        },
        checkedAllRows(e) {
            this.selected = this.tasks.length > 0 && e.target.checked;
            this.$emit("updateCheckedItems", {
                ...this.checkedItems,
                ...this.tasks.reduce((acc, task) => {
                    acc[task._id] = e.target.checked;
                    return acc;
                }, {}),
            });
        },
        deletePipeline() {
            if (!this.editMode) {
                this.$emit("close-create-mode");
            }
            this.editMode = false;
        },
        createPipeline() {
            if (!this.editMode) {
                this.$emit("create-pipeline", this.pipeline);
            } else {
                this.$emit("update-pipeline", this.pipeline);
                this.editMode = false;
            }
        },
        openEditMode() {
            if (!this.pipeline.isNewColumn) {
                this.editMode = !this.editMode;
            }
        },
        copyPipelineId() {
            const pipelineId = this.pipeline._id;
            if (pipelineId) {
                navigator.clipboard.writeText(pipelineId).then(() => {
                    this.$message.success("Pipeline ID kopyalandı");
                }).catch(() => {
                    this.$message.error("Pipeline ID kopyalanamadı");
                });
            } else {
                this.$message.error("Pipeline ID bulunamadı");
            }
        },
        isDeletedColumn() {
            return this.pipelines.filter((item) => item.type === this.pipeline.type).length > 1;
        },
        deleteColumn() {
            const isEmptyState = this.pipeline.taskCount === 0;
            if (!isEmptyState) {
                this.deleteTransportModal = true;
            } else {
                this.deleteColumnModal = true;
            }
        },
        deletePipelineFn(transport) {
            this.deleteColumnModal = false;
            this.deleteTransportModal = false;
            this.deletePipelineStage({
                pipelineId: this.pipeline._id,
                workflowId: this.workflowId,
                newTaskPositionId: transport ? this.newTaskPositionId : null,
            }).then(() => {
                this.$emit('pipeline-deleted');
                this.$notification.success({
                    message: 'Pipeline Silindi',
                    description: 'Pipeline başarıyla silindi.'
                });
            });
        },
        transportColumnVisibility() {
            this.transportTaskNewColumn = true;
            this.transportTaskBtn = false;
        },
        scrollListenerCallback(scrollTop, scrollHeight, clientHeight) {
            const loaded = this.tasks.length;
            const totalTask = this.pipeline.taskCount;
            const isExceeded = loaded >= totalTask;
            if (scrollTop + clientHeight >= scrollHeight * 0.99 && !isExceeded) {
                this.$emit("loadMoreTask", this.pipeline._id);
            }
        },
        initScrollListener() {
            if (!this.$refs.pipeline || !this.$refs.pipeline.$el) {
                return;
            }

            const fn = (e) => {
                this.scrollListenerCallback(e.target.scrollTop, e.target.scrollHeight, e.target.clientHeight);
            };
            this.$refs.pipeline.$el.addEventListener("scroll", fn);
            this.listeners.push(() => {
                this.$refs.pipeline.$el.removeEventListener("scroll", fn);
            });
        },
    },
    mounted() {
        const fn = (e) => {
            if (e.code === "Escape") {
                this.editMode = false;
                this.closeTaskForm();
            }
        };
        document.addEventListener("keydown", fn);
        this.listeners.push(() => {
            document.removeEventListener("keydown", fn);
        });
        this.cloneTasks = [...this.tasks];

        this.$nextTick(() => {
            this.initScrollListener();
        });
    },
    beforeDestroy() {
        this.listeners.forEach((fn) => fn());
    },
    watch: {
        checkedItems: {
            deep: true,
            handler() {
                const anyChecked =
                    this.cloneTasks.length > 0 && this.cloneTasks.some((task) => this.checkedItems[task._id]);
                this.selected =
                    this.cloneTasks.length > 0 && this.cloneTasks.every((task) => this.checkedItems[task._id]);
                this.indeterminate = anyChecked && !this.selected;
            },
        },
        tasks: {
            deep: true,
            handler() {
                this.cloneTasks = [...this.tasks];
            },
        },
        pipelineLoaded(newVal, oldVal) {
            if (newVal && newVal !== oldVal) {
                this.initScrollListener();
            }
        },
    },
};
</script>
<style lang="scss">
.drag-pipeline-columns {
    border: 3px dotted transparent;
    transition: all 0.3s ease;
    position: relative;
    display: inline-block;
    vertical-align: top;
    margin-right: 10px;
    height: calc(100vh - 180px);

    &.minimized {
        width: 40px !important;
        min-width: 40px;

        .column-content {
            display: none;
        }

        .minimize-toggle {
            display: flex;
            height: 100%;
        }
    }

    .minimize-toggle {
        display: none;
        flex-direction: column;
        align-items: center;
        height: 100%;
        min-height: 400px;
        background-color: #fff;
        border-radius: 4px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        cursor: pointer;
        border-top: 3px solid var(--pipeline-color, #4caf50);

        &:hover {
            background-color: #f5f5f5;
        }

        .minimize-toggle-icon {
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;

            .material-icons {
                font-size: 22px;
                color: #888;
            }
        }

        .minimize-toggle-count {
            margin: 5px 0;
            font-size: 18px;
            font-weight: 500;
            color: #888;
        }

        .minimize-toggle-title {
            writing-mode: vertical-rl;
            transform: rotate(180deg);
            text-orientation: mixed;
            padding: 10px 0;
            font-weight: 500;
            color: #333;
            max-height: 300px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &:hover {
        .basic-task-add {
            opacity: 1;
            visibility: visible;
        }
    }

    .column-content {
        height: 100%;
        display: flex;
        flex-direction: column;

        .drag-pipeline-rows {
            flex: 1;
            overflow-y: auto;
        }
    }

    .collapse-button {
        margin-left: 10px;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border-radius: 4px;
        background-color: #f5f5f5;
        border: 1px solid #d9d9d9;

        .material-icons {
            font-size: 20px;
            color: #666;
        }

        &:hover {
            background-color: #e6e6e6;
        }
    }

    .basic-task-add {
        opacity: 0;
        visibility: hidden;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        padding: 8px;
        cursor: pointer;

        &:hover {
            background-color: var(--color-bg-neutral-hover);
        }
    }

    .task-pipeline-type {
        width: 100%;
        margin-top: 10px;

        .classic-new-select {
            width: 100% !important;
            font-weight: bold;

            [role="combobox"] {
                padding: 4px;
                border: none !important;
                border-radius: 4px !important;
                background-color: var(--white) !important;
            }
        }
    }

    .supsis-draggable-card {
        background: var(--color-bg-neutral-default);
    }

    .pipeline-picker {
        width: 25px;
        height: 25px;
        border-radius: 4px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        input {
            position: absolute;
            z-index: 2;
        }

        .material-icons {
            z-index: 1;
            color: var(--row-color);
        }
    }
}

.kanban-task-transport-select {
    text-align: center !important;

    p {
        font-weight: bold !important;
        margin-bottom: 10px !important;
    }
}

.loading-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 15px 0;
    width: 100%;

    .loading-spinner {
        width: 24px;
        height: 24px;
        border: 3px solid rgba(0, 0, 0, 0.1);
        border-top: 3px solid var(--pipeline-color, #1890ff);
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        margin-bottom: 8px;
    }

    .loading-text {
        font-size: 12px;
        color: #5a5a5b;
    }
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.no-drag {
    user-select: none;
    cursor: default !important;

    touch-action: none;

    &.sortable-chosen {
        cursor: default !important;
    }
}
</style>
